import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import {
    Field,
    Form,
    FormElement,
    FormFieldSet,
    type FormRenderProps
} from "@progress/kendo-react-form";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { arrowRightIcon } from "@progress/kendo-svg-icons";
import { Link } from "react-router-dom";
import { useResetPasswordMutation } from "../../api/userApi";
import { emailValidator, FormInputField } from "../../components/kendo-form-controls";
import { Loader } from "@progress/kendo-react-indicators";

type ForgotPasswordFormValues = {
    email?: string;
};

const ForgotPassword: React.FC = () => {
    const [resetPassword, { isLoading }] = useResetPasswordMutation();
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const handleResetAsync = async (values: ForgotPasswordFormValues): Promise<void> => {
        setSuccessMessage(null);
        setErrorMessage(null);

        try {
            await resetPassword({ email: values.email ?? "" }).unwrap();
            setSuccessMessage("If an account exists for that email address, a password reset link will be sent shortly.");
        } catch {
            setErrorMessage("We couldn't process your request right now. Please try again later.");
        }
    };

    return (
        <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-body-tertiary px-3 py-4">
            <Card
                className="w-100 rounded-4 border border-secondary-subtle shadow-sm"
                style={{ maxWidth: "400px" }}
            >
                <CardBody className="px-4 py-5 p-sm-5">
                    <div className="mb-4">
                        <h1 className="h3 fw-bold mb-2 text-body">Forgot password</h1>
                        <p className="mb-0 text-body-secondary">
                            Enter your account email and we&apos;ll send a reset link.
                        </p>
                    </div>

                    <Form
                        onSubmit={(values: Record<string, unknown>) => void handleResetAsync(values as ForgotPasswordFormValues)}
                        render={(formRenderProps: FormRenderProps) => (
                            <FormElement>
                                {successMessage && (
                                    <div className="alert alert-success small mb-3" role="status">
                                        {successMessage}
                                    </div>
                                )}

                                {errorMessage && (
                                    <div className="alert alert-danger small mb-3" role="alert">
                                        {errorMessage}
                                    </div>
                                )}

                                {isLoading && (
                                    <div className="text-center">
                                        <Loader type="pulsing" size="large" />
                                    </div>
                                )}

                                {!successMessage && !isLoading && (
                                    <>
                                        <FormFieldSet className="mb-0">
                                            <Field
                                                id="reset-email"
                                                name="email"
                                                label="Work Email"
                                                component={FormInputField}
                                                validator={emailValidator}
                                            />
                                        </FormFieldSet>

                                        <Button
                                            type="submit"
                                            className="w-100 border-0 rounded-3 fw-semibold py-2 mt-3 shadow-sm"
                                            disabled={!formRenderProps.allowSubmit || isLoading}
                                            style={{ backgroundColor: "#2f8378", color: "#ffffff" }}
                                        >
                                            <span className="d-inline-flex align-items-center justify-content-center gap-2">
                                                {isLoading ? "Sending reset email" : "Send reset email"}
                                                {!isLoading && <SvgIcon icon={arrowRightIcon} style={{ width: "16px", height: "16px" }} />}
                                            </span>
                                        </Button>
                                    </>
                                )}

                                <p className="mb-0 mt-4 text-center text-body-secondary small">
                                    Remember your password?&nbsp;
                                    <Link className="link-success fw-semibold link-underline-opacity-0" to="/login">
                                        Back to login
                                    </Link>
                                </p>
                            </FormElement>
                        )}
                    />
                </CardBody>
            </Card>
        </div>
    );
};

export default ForgotPassword;
